import { useState, useEffect } from 'react'
import { DriverAnalysis } from '../types'
import { api } from '../api'
import { useAuth } from '../contexts/AuthContext'

const SEVERITY_STYLES: Record<string, { bg: string; text: string; border: string; label: string }> = {
  minor: { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-200', label: 'Minor' },
  serious: { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200', label: 'Serious' },
  very_serious: { bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-200', label: 'Very Serious' },
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  open: { label: 'Open', color: 'text-red-700', bgColor: 'bg-red-100' },
  under_review: { label: 'Under Review', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  disputed: { label: 'Disputed', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  resolved_no_action: { label: 'No Action Required', color: 'text-green-700', bgColor: 'bg-green-100' },
  resolved_action_taken: { label: 'Action Taken', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  closed: { label: 'Closed', color: 'text-gray-600', bgColor: 'bg-gray-200' },
}

interface NoteRecord {
  id: number
  infringement_date: string
  infringement_type: string
  note_text: string
  status: string
  severity?: string
  regulation?: string
  driver_explanation?: string
  dcrs_points_applied: boolean
  resolved_by?: string
  resolved_at?: string
  created_by: string
  created_at: string
}

export function InfringementsTab({ analysis, onNotesChanged }: { analysis: DriverAnalysis; onNotesChanged?: (notes: any[]) => void }) {
  const { user } = useAuth()
  const { infringements } = analysis
  const driverId = analysis.driver?.id
  const [notes, setNotes] = useState<NoteRecord[]>([])
  const [expandedInf, setExpandedInf] = useState<string | null>(null)
  const [newNote, setNewNote] = useState('')
  const [driverExplanation, setDriverExplanation] = useState('')
  const [saving, setSaving] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const isManager = user?.role === 'admin' || user?.role === 'transport_manager'
  const isDriver = user?.role === 'driver'

  useEffect(() => {
    if (driverId && driverId > 0) {
      api.getInfringementNotes(driverId).then(setNotes).catch(() => {})
    }
  }, [driverId])

  function infKey(inf: { date: string; type: string }) {
    return `${inf.date}::${inf.type}`
  }

  function getNoteFor(date: string, type: string): NoteRecord | undefined {
    return notes.find(n => n.infringement_date === date && n.infringement_type === type)
  }

  function getNotesFor(date: string, type: string): NoteRecord[] {
    return notes.filter(n => n.infringement_date === date && n.infringement_type === type)
  }

  function getStatusForInf(date: string, type: string): string {
    const note = getNoteFor(date, type)
    return note?.status || 'open'
  }

  async function handleAddNote(date: string, type: string, inf: any) {
    if (!newNote.trim()) return
    if (!driverId || driverId <= 0) {
      setError('Cannot save note — driver not linked to database. Please check driver record.')
      return
    }
    setError(null)
    setSaving(true)
    try {
      const note = await api.addInfringementNote({
        driver_id: driverId,
        infringement_date: date,
        infringement_type: type,
        note_text: newNote.trim(),
        severity: inf.severity,
        regulation: inf.regulation,
      })
      const updated = [note, ...notes]
      setNotes(updated)
      setNewNote('')
      setSuccessMsg('Note saved')
      setTimeout(() => setSuccessMsg(null), 2000)
      onNotesChanged?.(updated)
    } catch (err: any) {
      console.error('Failed to save note:', err)
      setError(err?.message || 'Failed to save note. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  async function handleStatusChange(noteId: number, newStatus: string) {
    setError(null)
    setSaving(true)
    try {
      const updated = await api.updateInfringementNote(noteId, { status: newStatus })
      setNotes(prev => {
        const newNotes = prev.map(n => n.id === noteId ? updated : n)
        onNotesChanged?.(newNotes)
        return newNotes
      })
      setSuccessMsg(`Status changed to ${STATUS_CONFIG[newStatus]?.label || newStatus}`)
      setTimeout(() => setSuccessMsg(null), 2000)
    } catch (err: any) {
      console.error('Status update failed:', err)
      setError(err?.message || 'Failed to update status. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  async function handleDriverExplanation(noteId: number) {
    if (!driverExplanation.trim()) return
    setError(null)
    setSaving(true)
    try {
      const updated = await api.updateInfringementNote(noteId, {
        driver_explanation: driverExplanation.trim(),
      })
      setNotes(prev => {
        const newNotes = prev.map(n => n.id === noteId ? updated : n)
        onNotesChanged?.(newNotes)
        return newNotes
      })
      setDriverExplanation('')
      setSuccessMsg('Explanation saved')
      setTimeout(() => setSuccessMsg(null), 2000)
    } catch (err: any) {
      console.error('Explanation submit failed:', err)
      setError(err?.message || 'Failed to save explanation. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteNote(noteId: number) {
    setError(null)
    try {
      await api.deleteInfringementNote(noteId)
      setNotes(prev => {
        const updated = prev.filter(n => n.id !== noteId)
        onNotesChanged?.(updated)
        return updated
      })
    } catch (err: any) {
      console.error('Delete failed:', err)
      setError(err?.message || 'Failed to delete note.')
    }
  }

  // Create a note record for an infringement if one doesn't exist yet
  async function ensureNoteExists(date: string, type: string, inf: any): Promise<NoteRecord | null> {
    const existing = getNoteFor(date, type)
    if (existing) return existing

    if (!driverId || driverId <= 0) {
      setError('Cannot create review record — driver not linked to database.')
      return null
    }
    setSaving(true)
    try {
      const note = await api.addInfringementNote({
        driver_id: driverId,
        infringement_date: date,
        infringement_type: type,
        note_text: `Review started: ${inf.description || type}`,
        severity: inf.severity,
        regulation: inf.regulation,
      })
      const updated = [note, ...notes]
      setNotes(updated)
      onNotesChanged?.(updated)
      return note
    } catch (err: any) {
      console.error('Failed to create infringement record:', err)
      setError(err?.message || 'Failed to create review record. Please try again.')
      return null
    } finally {
      setSaving(false)
    }
  }

  async function handleStatusChangeForInf(date: string, type: string, newStatus: string, inf: any) {
    // Ensure a note record exists, then update its status
    setError(null)
    const note = await ensureNoteExists(date, type, inf)
    if (!note) return // error already shown by ensureNoteExists
    try {
      const updated = await api.updateInfringementNote(note.id, { status: newStatus })
      setNotes(prev => {
        const newNotes = prev.map(n => n.id === note.id ? updated : n)
        onNotesChanged?.(newNotes)
        return newNotes
      })
      setSuccessMsg(`Status changed to ${STATUS_CONFIG[newStatus]?.label || newStatus}`)
      setTimeout(() => setSuccessMsg(null), 2000)
    } catch (err: any) {
      console.error('Status update failed:', err)
      setError(err?.message || 'Failed to update status. Please try again.')
    }
  }

  if (infringements.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-sm font-semibold text-green-700">No Infringements Detected</h3>
        <p className="text-gray-400 text-sm mt-1">All driving activity is within EC 561/2006 and WTD limits.</p>
      </div>
    )
  }

  const bySeverity = {
    very_serious: infringements.filter(i => i.severity === 'very_serious'),
    serious: infringements.filter(i => i.severity === 'serious'),
    minor: infringements.filter(i => i.severity === 'minor'),
  }

  // Count statuses
  const statusCounts = infringements.reduce((acc, inf) => {
    const s = getStatusForInf(inf.date, inf.type)
    acc[s] = (acc[s] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const filteredInfringements = statusFilter === 'all'
    ? infringements
    : infringements.filter(inf => getStatusForInf(inf.date, inf.type) === statusFilter)

  return (
    <div className="space-y-6">
      {/* Summary banner */}
      <div className="bg-red-50 border border-red-200 rounded p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-red-900">{infringements.length} infringement{infringements.length !== 1 ? 's' : ''} detected</h3>
            <p className="text-sm text-red-700 mt-0.5">
              {bySeverity.very_serious.length > 0 && `${bySeverity.very_serious.length} very serious · `}
              {bySeverity.serious.length > 0 && `${bySeverity.serious.length} serious · `}
              {bySeverity.minor.length > 0 && `${bySeverity.minor.length} minor`}
            </p>
          </div>
          <div className="text-right text-xs text-red-600">EC 561/2006 &amp; WTD</div>
        </div>

        {/* Status summary pills */}
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 text-xs rounded font-medium transition-colors ${statusFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-white/60 text-gray-600 hover:bg-white'}`}
          >
            All ({infringements.length})
          </button>
          {Object.entries(statusCounts).map(([status, count]) => {
            const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
                className={`px-3 py-1 text-xs rounded font-medium transition-colors ${statusFilter === status ? 'bg-gray-800 text-white' : `${cfg.bgColor} ${cfg.color} hover:opacity-80`}`}
              >
                {cfg.label} ({count})
              </button>
            )
          })}
        </div>
      </div>

      {/* Error / success banners */}
      {error && (
        <div className="bg-red-50 border border-red-300 rounded p-3 flex items-center justify-between">
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-3 text-xs font-medium">Dismiss</button>
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 border border-green-300 rounded p-3">
          <p className="text-sm text-green-800">{successMsg}</p>
        </div>
      )}

      {/* Infringement list */}
      <div className="space-y-3">
        {filteredInfringements.map((inf, i) => {
          const style = SEVERITY_STYLES[inf.severity] || SEVERITY_STYLES.minor
          const overMinutes = inf.value_minutes - inf.limit_minutes
          const overPct = ((inf.value_minutes / inf.limit_minutes) * 100 - 100).toFixed(0)
          const key = infKey(inf)
          const isExpanded = expandedInf === key
          const infNotes = getNotesFor(inf.date, inf.type)
          const currentStatus = getStatusForInf(inf.date, inf.type)
          const statusCfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.open
          const primaryNote = getNoteFor(inf.date, inf.type)

          return (
            <div key={i} className={`${style.bg} border ${style.border} rounded`}>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${style.text} ${style.bg} border ${style.border}`}>
                        {style.label}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusCfg.bgColor} ${statusCfg.color}`}>
                        {statusCfg.label}
                      </span>
                      {primaryNote?.dcrs_points_applied && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-200 text-orange-800">
                          DCRS Points Applied
                        </span>
                      )}
                      <span className="text-sm text-gray-500">{inf.date}</span>
                      <span className="text-xs text-gray-400">{inf.regulation}</span>
                    </div>
                    <p className={`text-sm font-medium ${style.text}`}>{inf.description}</p>
                  </div>
                  <div className="text-right ml-4 flex-shrink-0">
                    <p className={`text-lg font-bold ${style.text}`}>+{overMinutes}min</p>
                    <p className="text-xs text-gray-500">+{overPct}% over limit</p>
                  </div>
                </div>

                {/* Visual bar */}
                <div className="mt-3">
                  <div className="flex h-2 rounded overflow-hidden bg-white/50">
                    <div className="bg-green-400" style={{ width: `${(inf.limit_minutes / inf.value_minutes) * 100}%` }} />
                    <div className="bg-red-400" style={{ width: `${((inf.value_minutes - inf.limit_minutes) / inf.value_minutes) * 100}%` }} />
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-gray-500">
                    <span>0</span>
                    <span>Limit: {fmtMin(inf.limit_minutes)}</span>
                    <span>Actual: {fmtMin(inf.value_minutes)}</span>
                  </div>
                </div>

                {/* Driver explanation (if exists) */}
                {primaryNote?.driver_explanation && (
                  <div className="mt-3 bg-purple-50 border border-purple-200 rounded p-3">
                    <div className="text-xs font-bold text-purple-700 mb-1">Driver Explanation</div>
                    <p className="text-sm text-purple-900">{primaryNote.driver_explanation}</p>
                  </div>
                )}

                {/* Resolution info */}
                {primaryNote?.resolved_by && (
                  <div className="mt-2 text-xs text-gray-500">
                    Resolved by {primaryNote.resolved_by} on {primaryNote.resolved_at ? new Date(primaryNote.resolved_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </div>
                )}

                {/* Action buttons / status change */}
                <div className="mt-3 pt-2 border-t border-white/40 flex items-center justify-between flex-wrap gap-2">
                  <button
                    onClick={() => { setExpandedInf(isExpanded ? null : key); setNewNote(''); setDriverExplanation(''); }}
                    className="text-xs font-medium text-gray-600 hover:text-gray-900 flex items-center gap-1"
                  >
                    <svg className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Notes & Actions ({infNotes.length})
                  </button>

                  {/* Manager status controls */}
                  {isManager && currentStatus !== 'closed' && (
                    <div className="flex items-center gap-1">
                      {currentStatus === 'open' && (
                        <button
                          onClick={() => handleStatusChangeForInf(inf.date, inf.type, 'under_review', inf)}
                          className="px-2.5 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium"
                          disabled={saving}
                        >
                          Start Review
                        </button>
                      )}
                      {(currentStatus === 'under_review' || currentStatus === 'disputed') && (
                        <>
                          <button
                            onClick={() => handleStatusChangeForInf(inf.date, inf.type, 'resolved_no_action', inf)}
                            className="px-2.5 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 font-medium"
                            disabled={saving}
                            title="Accept driver explanation — no DCRS points"
                          >
                            No Action
                          </button>
                          <button
                            onClick={() => handleStatusChangeForInf(inf.date, inf.type, 'resolved_action_taken', inf)}
                            className="px-2.5 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 font-medium"
                            disabled={saving}
                            title="Genuine infringement — apply DCRS points"
                          >
                            Action Taken
                          </button>
                        </>
                      )}
                      {(currentStatus === 'resolved_no_action' || currentStatus === 'resolved_action_taken') && (
                        <button
                          onClick={() => handleStatusChangeForInf(inf.date, inf.type, 'closed', inf)}
                          className="px-2.5 py-1 text-xs bg-gray-200 text-gray-600 rounded hover:bg-gray-300 font-medium"
                          disabled={saving}
                        >
                          Close
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Expanded section */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-3">

                  {/* Driver explanation form (for drivers) */}
                  {isDriver && currentStatus !== 'closed' && currentStatus !== 'resolved_no_action' && currentStatus !== 'resolved_action_taken' && (
                    <div className="bg-white/70 rounded p-3 border border-purple-200">
                      <label className="text-xs font-bold text-purple-700 mb-1 block">Submit Your Explanation</label>
                      <p className="text-xs text-gray-500 mb-2">
                        If this infringement was a recording error (e.g., you took your break but forgot to switch the tachograph), explain what happened.
                        Your manager will review this.
                      </p>
                      <textarea
                        value={driverExplanation}
                        onChange={(e) => setDriverExplanation(e.target.value)}
                        placeholder="Explain what happened..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                        rows={3}
                      />
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={() => primaryNote && handleDriverExplanation(primaryNote.id)}
                          disabled={saving || !driverExplanation.trim() || !primaryNote}
                          className="px-4 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 font-medium"
                        >
                          {saving ? 'Submitting...' : 'Submit Explanation'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Manager explanation form */}
                  {isManager && (currentStatus === 'open' || currentStatus === 'under_review') && !primaryNote?.driver_explanation && (
                    <div className="bg-white/70 rounded p-3 border border-blue-200">
                      <label className="text-xs font-bold text-blue-700 mb-1 block">Add Driver Explanation (on behalf of driver)</label>
                      <p className="text-xs text-gray-500 mb-2">
                        Record the driver's verbal explanation from your meeting. DVSA expects documented evidence of driver discussions.
                      </p>
                      <textarea
                        value={driverExplanation}
                        onChange={(e) => setDriverExplanation(e.target.value)}
                        placeholder="Driver stated that..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                        rows={2}
                      />
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={async () => {
                            const note = await ensureNoteExists(inf.date, inf.type, inf)
                            if (note) await handleDriverExplanation(note.id)
                          }}
                          disabled={saving || !driverExplanation.trim()}
                          className="px-4 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-medium"
                        >
                          {saving ? 'Saving...' : 'Record Explanation'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Existing notes */}
                  {infNotes.length > 0 && (
                    <div className="space-y-2">
                      {infNotes.map(note => (
                        <div key={note.id} className="bg-white/60 rounded p-3 text-sm">
                          <div className="flex items-start justify-between">
                            <p className="text-gray-800">{note.note_text}</p>
                            {isManager && (
                              <button onClick={() => handleDeleteNote(note.id)} className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0" title="Delete note">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {note.created_by} · {note.created_at ? new Date(note.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add note form */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote(inf.date, inf.type, inf)}
                      placeholder="Add a note..."
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    />
                    <button
                      onClick={() => handleAddNote(inf.date, inf.type, inf)}
                      disabled={saving || !newNote.trim()}
                      className="px-4 py-2 text-sm bg-gray-800 text-white rounded hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? '...' : 'Add'}
                    </button>
                  </div>

                  {/* Status change dropdown for managers (advanced) */}
                  {isManager && primaryNote && (
                    <div className="bg-white/50 rounded p-3 border border-gray-200">
                      <div className="flex items-center gap-3">
                        <label className="text-xs font-medium text-gray-500">Change Status:</label>
                        <select
                          value={currentStatus}
                          onChange={(e) => handleStatusChange(primaryNote.id, e.target.value)}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded bg-white"
                          disabled={saving}
                        >
                          <option value="open">Open</option>
                          <option value="under_review">Under Review</option>
                          <option value="disputed">Disputed</option>
                          <option value="resolved_no_action">Resolved - No Action</option>
                          <option value="resolved_action_taken">Resolved - Action Taken (DCRS Points)</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* DVSA guidance */}
      <div className="bg-blue-50 border border-blue-200 rounded p-4">
        <h4 className="text-xs font-bold text-blue-800 mb-2">DVSA Compliance Guidance</h4>
        <div className="text-xs text-blue-700 space-y-1">
          <p>Operators must review <strong>every infringement</strong> detected from driver card downloads within 28 days.</p>
          <p>Hold a documented meeting with the driver. Record their explanation — even if the infringement was a tachograph recording error (e.g., forgot to switch to break mode).</p>
          <p><strong>"No Action"</strong> = explanation accepted, no DCRS points. Use for recording errors where the driver can demonstrate they were compliant.</p>
          <p><strong>"Action Taken"</strong> = genuine infringement, DCRS points applied automatically. This feeds into your disciplinary procedure levels.</p>
          <p>Keep all records for at least <strong>12 months</strong>. DVSA traffic examiners will check your infringement review process during compliance visits.</p>
        </div>
      </div>
    </div>
  )
}

function fmtMin(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}
